import {
  Grid,
  Box,
  useTheme,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Common } from "../../../common/src";
import Project from "./parts/Project";
import { Dispatch, SetStateAction, memo, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { EViewType } from "../../pages/Home";

// Ước lượng chiều cao 1 dòng ở List View (Project.tsx, nhánh List View) — dùng để
// giữ chỗ chiều cao gần đúng cho khối loading, tránh giật/nhảy scroll khi
// ClusteringItems chuyển qua lại giữa trạng thái loading và nội dung thật.
const LIST_VIEW_ROW_HEIGHT = 108;

type PropsClustering = {
  index: string;
  displayProjects: { [name: string]: [Common.Project] };
  jobsiteList: Common.LandlogJobSite[];
  userList: Common.ProjectUserList[];
  selectProject: Common.Project[];
  setSelectProject: (project: Common.Project[]) => void;
  isLanguage: boolean;
  setErrorSnackbar: Dispatch<SetStateAction<boolean>>;
  setErrorMsg: Dispatch<SetStateAction<string>>;
  setOpenWarning: Dispatch<SetStateAction<boolean>>;
  setMsgWarning: Dispatch<SetStateAction<string>>;
  isARAvailable: boolean;
  viewType: EViewType;
  sortBy?: string;
};

export const ClusteringItemsComponent = (props: PropsClustering) => {
  // key毎に分けてコンポーネントを作成する
  const displayProjects = props.displayProjects;
  let index = props.index;

  // triggerOnce: một khi cluster đã từng vào view thì coi như luôn "đã thấy",
  // không ẩn lại thành loading nữa dù sau đó cuộn ra khỏi view — tránh mất
  // trạng thái tương tác dở dang (menu đang mở...) bên trong các Project con.
  const { ref, inView } = useInView({ threshold: 0, triggerOnce: true });
  const isListView = props.viewType === EViewType.LIST;
  const showLoading = isListView && !inView;

  // Memoize cluster creation để tránh re-create Project components mỗi render
  const cluster = useMemo(() => {
    return displayProjects[index].map((project) => (
      <Project
        project={project}
        selectProject={props.selectProject}
        setSelectProject={props.setSelectProject}
        jobsiteNameList={props.jobsiteList}
        userNameList={props.userList}
        key={project.id}
        isLanguage={props.isLanguage}
        setErrorSnackbar={props.setErrorSnackbar}
        setErrorMsg={props.setErrorMsg}
        setOpenWarning={props.setOpenWarning}
        setMsgWarning={props.setMsgWarning}
        isARAvailable={props.isARAvailable}
        viewType={props.viewType}
        sortBy={props.sortBy}
      />
    ));
  }, [
    displayProjects,
    index,
    props.selectProject,
    props.setSelectProject,
    props.jobsiteList,
    props.userList,
    props.isLanguage,
    props.setErrorSnackbar,
    props.setErrorMsg,
    props.setOpenWarning,
    props.setMsgWarning,
    props.isARAvailable,
    props.viewType,
  ]);

  // 日付変換
  let dateIndex = new Date(index);
  let formattedDate;

  if (props.isLanguage) {
    if (index.match(/\d{4}$/)) {
      formattedDate = dateIndex.getFullYear();
    } else if (index.match(/\d{4}\/?\d{2}$/)) {
      formattedDate =
        dateIndex.getFullYear() +
        "/" +
        ("0" + (dateIndex.getMonth() + 1)).slice(-2);
    } else if (index.match(/\d{4}\/?\d{2}\/?\d{2}$/)) {
      formattedDate =
        dateIndex.getFullYear() +
        "/" +
        ("0" + (dateIndex.getMonth() + 1)).slice(-2) +
        "/" +
        ("0" + dateIndex.getDate()).slice(-2);
    }
  } else {
    if (index.match(/\d{4}$/)) {
      formattedDate = dateIndex.getFullYear();
    } else if (index.match(/\d{4}\/?\d{2}$/)) {
      formattedDate =
        ("0" + (dateIndex.getMonth() + 1)).slice(-2) +
        "/" +
        dateIndex.getFullYear();
    } else if (index.match(/\d{4}\/?\d{2}\/?\d{2}$/)) {
      formattedDate =
        ("0" + dateIndex.getDate()).slice(-2) +
        "/" +
        ("0" + (dateIndex.getMonth() + 1)).slice(-2) +
        "/" +
        dateIndex.getFullYear();
    }
  }
  index =
    formattedDate !== undefined && formattedDate !== null
      ? formattedDate.toString()
      : "";

  const theme = useTheme();

  const sectionHeaderStyle = {
    fontSize: "18px",
    fontWeight: 600,
    color: theme.palette.mode === "dark" ? "#FFFFFF" : "#1a1a1a",
    marginBottom: props.viewType === EViewType.LIST ? "12px" : "16px",
    paddingLeft: props.viewType === EViewType.LIST ? "20px" : "0px",
    borderBottom: "none",
    paddingBottom: "0px",
  };

  const clusterItemCount = displayProjects[props.index]?.length ?? 0;

  return (
    <Grid
      container
      ref={ref}
      sx={{
        marginBottom: props.viewType === EViewType.LIST ? "24px" : "32px",
        alignContent: "flex-start",
        justifyContent: "flex-start",
      }}
    >
      <Grid container>
        <Grid item xs={12}>
          <Typography component="h3" sx={sectionHeaderStyle}>
            {index}
          </Typography>
        </Grid>
      </Grid>
      <Box sx={{ flexGrow: 1, width: "100%" }}>
        {showLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: clusterItemCount * LIST_VIEW_ROW_HEIGHT,
              width: "100%",
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Grid
            container
            spacing={props.viewType === EViewType.LIST ? 0 : { xs: 1, md: 1 }}
            columns={{ xs: 3, sm: 8, md: 12 }}
            sx={{
              padding: props.viewType === EViewType.LIST ? "0" : undefined,
            }}
          >
            {cluster}
          </Grid>
        )}
      </Box>
    </Grid>
  );
};

// Wrap với memo để tránh re-render không cần thiết
export const ClusteringItems = memo(ClusteringItemsComponent);
